<template>
  <component
    :is="tag"
    :class="[
      'base-btn inline-flex items-center justify-center font-medium whitespace-nowrap gap-1 select-none',
      elementClass,
      isIconOnly ? 'p-[7px]' : 'px-4 py-2',
      isWidth ? 'w-[160px]' : '',
      isHeight ? 'h-[45px]' : '',
      isCircle ? 'rounded-full' : 'rounded-md',
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      size === 'xs' ? 'text-[11px]' : '',
      size === 'sm' ? 'text-sm' : '',
      size === 'md' ? 'text-[15px]' : '',
      size === 'lg' ? 'text-lg' : '',
      className,
    ]"
    :style="baseBgStyle"
    :disabled="disabled || isLoading"
    v-bind="$attrs"
  >
    <span
      class="flex justify-center items-center flex-nowrap whitespace-nowrap gap-1 font-medium"
      :class="{ 'opacity-80': isLoading }"
    >
      <slot v-if="!isLoading" name="right" />
      <slot v-if="!isIconOnly && !isLoading" />
      <slot v-if="!isLoading" name="left" />
    </span>

    <span v-if="isLoading" class="loading loading-spinner text-[1.05em]"></span>
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

interface Props {
  tag?: 'button' | 'span' | 'RouterLink' | 'a'
  variant?: 'orange' | 'purple' | 'teal' | 'blue' | 'red' | 'outlines' | 'trans' | 'input' | 'black' | 'amd' | 'rani' | 'base' | 'ghost' | 'danger'
  bg?: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
  isIconOnly?: boolean
  isCircle?: boolean
  isLoading?: boolean
  disabled?: boolean
  className?: string
  isWidth?: boolean
  isHeight?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  tag: 'button',
  variant: 'base',
  bg: '',
  size: 'sm',
  isIconOnly: false,
  isCircle: false,
  isLoading: false,
  disabled: false,
  className: '',
  isWidth: false,
  isHeight: false,
})

const { BASE_COLOR } = CONFIG

/* variant class map */
const elementClass = computed(() => ({
  orange: props.variant === 'orange',
  purple: props.variant === 'purple',
  teal: props.variant === 'teal',
  blue: props.variant === 'blue',
  red: props.variant === 'red',
  outlines: props.variant === 'outlines',
  trans: props.variant === 'trans',
  input: props.variant === 'input',
  black: props.variant === 'black',
  amd: props.variant === 'amd',
  rani: props.variant === 'rani',
  base: props.variant === 'base',
  ghost: props.variant === 'ghost',
  danger: props.variant === 'danger',
}))

/* dynamic bg for base variant */
const baseBgStyle = computed(() => {
  if (props.variant !== 'base') return {}

  return {
    backgroundColor: props.bg || BASE_COLOR,
  }
})
</script>

<style scoped>
/* Shared press feedback: buttons must feel responsive to press */
.base-btn {
  transition: transform var(--duration-fast) var(--ease-out),
              background-color var(--duration-fast) var(--ease-out),
              box-shadow var(--duration-fast) var(--ease-out),
              border-color var(--duration-fast) var(--ease-out),
              opacity var(--duration-fast) var(--ease-out);
}

.base-btn:active:not(:disabled) {
  transform: scale(0.97);
}

/* --- Variant Styles --- */
.black {
  background-color: #000;
  color: white;
  box-shadow: var(--shadow-xs);
}
.black:hover:not(:disabled) {
  background-color: #1a1a1a;
  box-shadow: var(--shadow-sm);
}

.trans {
  background-color: transparent;
  color: var(--color-font-dark);
}
.trans:hover:not(:disabled) {
  background-color: var(--color-bg-input);
}

.orange {
  background-color: var(--color-m-orange);
  color: white;
  box-shadow: var(--shadow-xs);
}
.orange:hover:not(:disabled) {
  background-color: #e67e00;
  box-shadow: var(--shadow-sm);
}

.purple {
  background-color: var(--color-m-purple);
  color: white;
  box-shadow: var(--shadow-xs);
}
.purple:hover:not(:disabled) {
  background-color: #6a3ce0;
  box-shadow: var(--shadow-sm);
}

.teal {
  background-color: var(--color-m-teal);
  color: white;
  box-shadow: var(--shadow-xs);
}
.teal:hover:not(:disabled) {
  background-color: #00796b;
  box-shadow: var(--shadow-sm);
}

.blue {
  background-color: rgba(59, 130, 246, 0.12);
  color: #2563eb;
  border: 1px solid rgba(59, 130, 246, 0.2);
}
.blue:hover:not(:disabled) {
  background-color: rgba(59, 130, 246, 0.2);
  border-color: rgba(59, 130, 246, 0.35);
}

.red {
  background-color: rgba(239, 68, 68, 0.12);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.2);
}
.red:hover:not(:disabled) {
  background-color: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.35);
}

.input {
  background-color: var(--color-bg-input);
  color: var(--color-m-blue);
}
.dark .input {
  color: white;
}

.amd {
  background-color: #93103c;
  color: white;
  box-shadow: var(--shadow-xs);
}

.rani {
  background-color: #fc8310;
  color: white;
  box-shadow: var(--shadow-xs);
}

.outlines {
  background-color: var(--color-surface-raised);
  border: 1px solid var(--color-border);
  color: var(--color-font-dark);
}
.outlines:hover:not(:disabled) {
  border-color: var(--color-border-strong);
  background-color: var(--color-surface);
}

.base {
  color: white;
  box-shadow: var(--shadow-xs);
}
.base:hover:not(:disabled) {
  filter: brightness(1.08);
  box-shadow: var(--shadow-sm);
}

/* New variants */
.ghost {
  background-color: transparent;
  border: 1px solid transparent;
  color: var(--color-font-dark);
}
.ghost:hover:not(:disabled) {
  background-color: var(--color-bg-input);
  border-color: var(--color-border);
}

.danger {
  background-color: #dc2626;
  color: white;
  box-shadow: var(--shadow-xs);
}
.danger:hover:not(:disabled) {
  background-color: #b91c1c;
  box-shadow: var(--shadow-sm);
}

/* Focus visible for keyboard accessibility */
button:focus-visible,
a:focus-visible {
  outline: 2px solid var(--color-brand);
  outline-offset: 2px;
}
</style>
